<template>
  <div>
    <div class="d-flex mb-1">
      <PhysicianPickerWindow @physicianSelected="onPhysicianSelected" />
      <InputText
        v-model="orderId"
        class="ml-2"
        type="search"
        :placeholder="t('Order ID')"
        hide-details="auto"
        clearable
        variant="outlined"
        @keydown="watchUserSubmit"
        @focus="onOrderIdFocus"
        @click:clear="clearOrderId"
      />
      <InputText
        v-model="cegatId"
        class="ml-2"
        type="search"
        :placeholder="t('Cegat ID')"
        hide-details="auto"
        clearable
        variant="outlined"
        @keydown="watchUserSubmit"
        @focus="onCegatIdFocus"
        @click:clear="clearCegatId"
      />
      <Button
        class="ml-2"
        @click="handleLoadClick"
      >
        {{ t("Load order") }}
      </Button>
    </div>
    <div
      v-if="selectedPhysician.userId"
      class="mb-3 font-weight-bold"
    >
      {{ t("Orders for") }}: {{ selectedPhysician.firstName }} {{ selectedPhysician.lastName }}

      <Button
        color="red"
        class="ml-2 btn-fab--x-small"
        :icon-btn="true"
        :title="$t('Clear physician')"
        size="x-small"
        variant="outlined"
        density="comfortable"
        @click="onPhysicianDeselected"
      >
        <v-icon
          color="red"
        >
          mdi-close
        </v-icon>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Button from "@/components/input-components/Button.vue";
import InputText from "@/components/input-components/InputText.vue";
import PhysicianPickerWindow from "@/components/patients/PhysicianPickerWindow.vue";
import { Physician } from "@/entities/physician";
import { usePhysiciansStore } from '@/stores/physicians';
import { usePatientsStore } from '@/stores/patients';
import { useOrdersStore } from '@/stores/orders';
import { useI18n } from 'vue-i18n'
import { ref, computed, onMounted } from 'vue'

defineOptions({
  name: 'OrdersToolbarInternal'
})

type SearchField = 'orderId' | 'cegatId';

const props = defineProps<{ orderIdPreFilter?: string }>()
const { t } = useI18n()

const ordersStore = useOrdersStore()
const physiciansStore = usePhysiciansStore()
const patientsStore = usePatientsStore()

const orderId = ref(props.orderIdPreFilter ?? "")
const cegatId = ref("")
const orderIdFocus = ref(false)
const selectedPhysician = computed<Physician>(() =>
  physiciansStore.getPhysician || { userId: "", firstName: "", lastName: "", email: "", city: "", isVerified: true }
)

const contextKey = computed(
  () => selectedPhysician.value?.userId || 'all'
);

async function loadOrdersBasedOnCurrentFilters() {
  ordersStore.searchOrderId = orderId.value
  ordersStore.searchCegatId = cegatId.value

  if (selectedPhysician.value.userId) {
    await ordersStore.fetchOrdersByPhysicianId(selectedPhysician.value.userId)
  } else if (orderId.value) {
    await ordersStore.fetchOrdersByOrderId(orderId.value)
  } else if (cegatId.value) {
    await ordersStore.fetchOrdersByCegatId(cegatId.value)
  } else {
    ordersStore.searchOrderId = ''
    ordersStore.searchCegatId = ''
    await ordersStore.fetchOrders(true)
  }
}

async function loadAllPhysiciansAndPatients(physician: Physician) {
  await clearOrderId();
  await clearCegatId();
  await patientsStore.fetchPatientsByPhysicianId(physician.userId);
  physiciansStore.physician = physician;

  const cached = ordersStore.restoreOrdersFromCache(physician.userId)
  if (!cached) {
    await ordersStore.fetchOrdersByPhysicianId(physician.userId);
  }
}

async function loadOrdersByOrderId() {
  await searchByField('orderId', orderId.value, true);
}

async function loadOrdersByCegatId() {
  await searchByField('cegatId', cegatId.value, false);
}

async function onPhysicianSelected(physician: Physician): Promise<void> {
  await loadAllPhysiciansAndPatients(physician)
}

async function onPhysicianDeselected(): Promise<void> {
  clearPhysician()
  await loadOrdersBasedOnCurrentFilters()
}

function clearPhysician() {
  physiciansStore.resetPhysician()
  ordersStore.resetOrders()
  ordersStore.searchInput = ''
}

async function searchByField(field: SearchField, value: string, focus: boolean): Promise<void> {
  orderIdFocus.value = focus;

  if (field === 'orderId') {
    cegatId.value = '';
    ordersStore.setSearchCegatId('');
  } else {
    orderId.value = '';
    ordersStore.setSearchOrderId('');
  }

  if (!value) {
    await clearSearchField(field);
    return;
  }

  const filtered = ordersStore.findCachedOrders(field, value);

  if (filtered.length > 0) {
    ordersStore.setOrders(filtered);
    setSearchFieldValue(field, value);
  } else {
    setSearchFieldValue(field, value);
    if (field === 'orderId') {
      await ordersStore.fetchOrdersByOrderId(value);
    } else {
      await ordersStore.fetchOrdersByCegatId(value);
    }
  }
}

function setSearchFieldValue(field: SearchField, value: string): void {
  if (field === 'orderId') {
    ordersStore.setSearchOrderId(value);
  } else {
    ordersStore.setSearchCegatId(value);
  }
}

async function clearSearchField(field: SearchField): Promise<void> {
  if (field === 'orderId') {
    orderId.value = '';
    ordersStore.setSearchOrderId('');
  } else {
    cegatId.value = '';
    ordersStore.setSearchCegatId('');
  }

  ordersStore.setSearchInput('');

  const restored = ordersStore.restoreOrdersFromCache(contextKey.value);
  if (!restored) {
    await loadAllOrdersForCurrentContext();
  }
}

async function clearOrderId() {
  await clearSearchField('orderId');
}

async function clearCegatId() {
  await clearSearchField('cegatId');
}

function onOrderIdFocus() {
  orderIdFocus.value = true
  cegatId.value = ''
  ordersStore.setSearchCegatId('')
}

function onCegatIdFocus() {
  orderIdFocus.value = false
  orderId.value = ''
  ordersStore.setSearchOrderId('')
}

function watchUserSubmit(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    if (orderIdFocus.value) {
      loadOrdersByOrderId()
    } else {
      loadOrdersByCegatId()
    }
  }
}

function handleLoadClick() {
  if (orderIdFocus.value) {
    loadOrdersByOrderId()
  } else {
    loadOrdersByCegatId()
  }
}

async function loadAllOrdersForCurrentContext() {
  const restored = ordersStore.restoreOrdersFromCache(contextKey.value);
  if (!restored) {
    if (selectedPhysician.value?.userId) {
      await ordersStore.fetchOrdersByPhysicianId(selectedPhysician.value.userId);
    } else {
      await ordersStore.fetchOrders(true);
    }
  }
}

onMounted(async () => {
  orderId.value = props.orderIdPreFilter ?? '';
  await loadAllOrdersForCurrentContext();
  if (orderId.value) {
    await searchByField('orderId', orderId.value, true);
  }
});
</script>
